/**
 * Aircraft tracking rules (phase 2b.4; docs/specs/tracker.md; Diurnal inventory 03). The common position model every
 * tracker adapter produces, the tenant's tracker assets, airborne / stale / lost-tracking / SOS states, trails and
 * their retention, one icon per aircraft across sources, RFDS marking, and what a client may see.
 *
 * MB's decisions: clients see public traffic around their survey (Q36, decision 63); clients see position, take-off,
 * flight time and landing, never SOS or lost tracking (Q37, decision 64); positions kept up to 48 hours (Q48,
 * decision 75); lost-tracking alert minutes are a tenant setting, 15 by default for every provider (Q44, decision 71);
 * RFDS marking per tenant (Q46, decision 73); clients also see field vehicles assigned to their project (Q47,
 * decision 74); the tracker's asset list is its own, not the project aircraft register (Q42, decision 69).
 */
import { auAerodromeList } from './aerodromes.js';
import type { AircraftKind, AircraftPosition } from './aircraft-layer.js';
import type { AirframeClass } from './aircraft-register.js';
import type { AircraftColour } from './map-layers.js';

/** Fleet trackers (tenant accounts through the provider proxy) and public traffic feeds. */
export type FleetProviderId = 'v2track' | 'spidertracks' | 'websentinel' | 'tracplus';
/** `bushfireio`: bushfire.io's fire aircraft (Thomson only, decision 476; docs/specs/map.md §31). */
export type PublicTrafficId = 'opensky' | 'adsb' | 'ozrunways' | 'avplan' | 'bushfireio';
export type TrackerSourceId = FleetProviderId | PublicTrafficId;

export const FLEET_PROVIDERS: readonly FleetProviderId[] = [
  'v2track',
  'spidertracks',
  'websentinel',
  'tracplus',
];

export const PUBLIC_TRAFFIC: readonly PublicTrafficId[] = [
  'opensky',
  'adsb',
  'ozrunways',
  'avplan',
  'bushfireio',
];

/** Whether a source is public air traffic (not one of the organisation's trackers). */
export const isPublicTraffic = (s: TrackerSourceId): s is PublicTrafficId =>
  (PUBLIC_TRAFFIC as readonly string[]).includes(s);

/**
 * Public traffic counts as old after 20 minutes (the Diurnal tool's Oz Runways and AvPlan rule, am:9658, 9103). It
 * is never "lost tracking" or SOS: those words are for the organisation's own aircraft only.
 */
export const PUBLIC_STALE_MINUTES = 20;

export const TRACKER_SOURCE_NAME: Readonly<Record<TrackerSourceId, string>> = {
  v2track: 'V2Track',
  spidertracks: 'Spider Tracks',
  websentinel: 'WebSentinel',
  tracplus: 'TracPlus',
  opensky: 'OpenSky',
  adsb: 'Community ADS-B',
  ozrunways: 'Oz Runways',
  avplan: 'AvPlan',
  bushfireio: 'bushfire.io',
};

/** One fix. Times are epoch ms UTC; altitude feet, speed knots, heading degrees true. */
export interface TrackPoint {
  readonly lat: number;
  readonly lon: number;
  readonly timeMs: number;
  readonly altFt: number | null;
  readonly speedKt: number | null;
  readonly headingDeg: number | null;
  /** The tracker's event at this point, in its words ("Take Off", "Enroute", "Landing"), when it names one. */
  readonly event?: string | null;
}

/** The common position model of every adapter (inventory 03 §18 criterion 1). */
export interface TrackedPosition extends TrackPoint {
  readonly source: TrackerSourceId;
  /** The tenant's account label for that provider ('' for the only one); '' for public traffic. */
  readonly account: string;
  /** The provider's id of the device or aircraft (V2Track GUID, Spider Tracks registration or IMEI, WebSentinel
   * vehicle id, TracPlus folder name, ADS-B hex). */
  readonly deviceId: string;
  readonly label: string;
  readonly registration: string | null;
  readonly icaoHex: string | null;
  /** Null when the provider does not say (the asset or the default decides). */
  readonly kind: AircraftKind | null;
  readonly fleetGroup: string;
  readonly sos: boolean;
  /** True on a satellite link (V2Track `aff`, Spider Tracks `IRIDIUM`), false on cellular, null unknown. */
  readonly satellite: boolean | null;
  /** The provider's own flying flag (Spider Tracks `FLYING`; V2Track's status words at the fix, `withStatus`), null
   * when it has none: the speed then decides (`isAirborne`). */
  readonly providerFlying: boolean | null;
  readonly tripId: string | null;
  /** On NAFC's fire aircraft list (decision 404, `nafc.ts`): its call-sign, registration, category, make and model. */
  readonly fire?: {
    readonly callsign: string;
    readonly registration: string;
    readonly category: string;
    readonly make: string;
    readonly model: string;
    /** NAFC's role ("Rotary Wing Type 3"), from bushfire.io only. */
    readonly role?: string;
    /** The operator, from bushfire.io only. */
    readonly operator?: string;
  } | null;
}

/** A tracker asset of the tenant (synced table `tracker_asset`). */
export interface TrackerAsset {
  readonly id: string;
  readonly tenant_id: string;
  readonly provider: FleetProviderId;
  readonly account_label: string;
  readonly device_id: string;
  readonly label: string;
  readonly registration: string;
  readonly icao_hex: string;
  readonly kind: AircraftKind;
  /** A brand token name for the aircraft's colour ('accent' by default). */
  readonly colour: string;
  readonly fleet_group: string;
  readonly aircraft_id: string | null;
  readonly hidden: boolean;
  readonly deleted_at: string | null;
}

/** An asset on a project for a time window (synced table `tracker_assignment`): what that project's client sees. */
export interface TrackerAssignment {
  readonly id: string;
  readonly project_id: string;
  readonly asset_id: string;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly deleted_at: string | null;
}

/** The tenant's tracking settings (synced table `tracking_settings`). */
export interface TrackingSettings {
  readonly lost_tracking_minutes: number;
  /** Per-provider overrides of the lost-tracking minutes, set by a tenant admin. */
  readonly provider_minutes: Readonly<Partial<Record<FleetProviderId, number>>>;
  readonly clients_see_public_traffic: boolean;
  /** Source order for one icon per aircraft (highest first); empty: the default. */
  readonly source_priority: readonly TrackerSourceId[];
  /** R184: minutes airborne without V2Track flight watch before its alert (null or absent: 10). */
  readonly flight_watch_start_minutes?: number | null;
  /** R184: minutes landed with V2Track flight watch still on before its alert (null or absent: 15). */
  readonly flight_watch_stop_minutes?: number | null;
}

/** Q44: 15 minutes for every provider unless the tenant admin changes it. */
export const DEFAULT_LOST_TRACKING_MINUTES = 15;

export const DEFAULT_TRACKING_SETTINGS: TrackingSettings = {
  lost_tracking_minutes: DEFAULT_LOST_TRACKING_MINUTES,
  provider_minutes: {},
  clients_see_public_traffic: true,
  source_priority: [],
};

/** Q48: positions and trails are kept up to 48 hours. */
export const TRAIL_RETENTION_HOURS = 48;
export const TRAIL_RETENTION_MS = TRAIL_RETENTION_HOURS * 3_600_000;
/** History depth choices (the Diurnal steps, inventory 03 §7), up to the retention. */
export const HISTORY_DEPTH_HOURS = [1, 2, 4, 6, 12, 24, 48] as const;
/** Points kept per aircraft (the Diurnal stores capped at 2000; a whole Spider Tracks flight can be 5000). */
export const TRAIL_MAX_POINTS = 5000;

export function lostTrackingMinutes(settings: TrackingSettings, provider: TrackerSourceId): number {
  const own = (settings.provider_minutes as Partial<Record<string, number>>)[provider];
  const m = own ?? settings.lost_tracking_minutes;
  return Number.isFinite(m) && m >= 1 ? m : DEFAULT_LOST_TRACKING_MINUTES;
}

/** Check a lost-tracking setting: whole minutes from 1 to 240. */
export function checkLostTrackingMinutes(v: number): string | null {
  return Number.isInteger(v) && v >= 1 && v <= 240 ? null : 'Enter whole minutes from 1 to 240.';
}

// ---------------------------------------------------------------------------------------------- states

/** Ground speed at or above which an aircraft counts as airborne (am:2191–2192): fixed-wing 40 kt, helicopter 20 kt. */
export const AIRBORNE_KT: Readonly<Record<AircraftKind, number>> = {
  fixed_wing: 40,
  helicopter: 20,
  ground: Infinity,
};

export type TrackerState = 'sos' | 'lost' | 'stale' | 'airborne' | 'parked';

export function isAirborne(
  p: Pick<TrackedPosition, 'providerFlying' | 'speedKt'>,
  kind: AircraftKind,
): boolean {
  if (p.providerFlying !== null) return p.providerFlying;
  return (p.speedKt ?? 0) >= AIRBORNE_KT[kind];
}

// ------------------------------------------------------------------------------------- the tracker's status words

/**
 * The status words the trackers put on their reports, as the genuine captures show them. V2Track trip points (`e`;
 * codes `ev`; VH-JEI 28 Oct 2026, VH-JOZ 19 Oct): on the ground "Start Up" (1), "Stationary", "Taxiing" (3),
 * "Landing" (5), "Tracker Power Up" (96), "Tracker Power Down" (95); flying "Take Off" (2), "Enroute" (3). TracPlus
 * placemark balloons ("Last Event"; VH-JEI and VH-JHF, 19 and 20 Oct 2026): "Startup", "Monitoring Request",
 * "Take-Off", "Standard Report", "Course Change", "Monitoring Cancellation", "Landing". Spider Tracks point
 * descriptions (P2-PHU, 19 Oct 2026): "Engine On", "Take Off", "Heading Change", "Landing", "Engine Off". Words are
 * compared without case, hyphens or extra spaces, so "Take-Off" and "Take Off" are one word.
 */
const GROUND_WORDS: ReadonlySet<string> = new Set([
  'start up',
  'startup',
  'engine on',
  'engine off',
  'stationary',
  'taxiing',
  'landing',
  'tracker power up',
  'tracker power down',
]);
const TAKE_OFF_WORDS: ReadonlySet<string> = new Set(['take off', 'takeoff']);
/** Status words older than this before a fix say nothing about it (V2Track reports every 2 minutes in flight). */
const STATUS_MAX_AGE_MS = 10 * 60_000;
/** A report under this many times the kind's airborne speed, at the ground level, is a landing roll. */
const LANDING_ROLL_SPEED_FACTOR = 1.5;
/** How far from the trip's ground level (feet) such a slow "Enroute" report still counts as on the ground. */
const GROUND_LEVEL_FT = 150;
/** A report this far above the Landing means the aircraft flew again without a "Take Off" word. */
const CLIMBED_FT = 500;

const statusWord = (p: TrackPoint): string =>
  (p.event ?? '').trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');

/**
 * Whether the tracker's own status words say the aircraft was flying at the fix of `atMs` (decision 860, issue #70),
 * or null when they do not say (no words within 10 minutes before the fix, or only report words such as TracPlus's
 * "Standard Report": the landing evidence, then the speed, decide). The latest word at or before the fix decides: a
 * ground word (Landing, Taxiing, Stationary, Start Up, Engine On or Off, Tracker Power Up or Down) is on the ground;
 * "Take Off" is flying; any other word after a "Landing" with no "Take Off" since is still on the ground (the genuine
 * V2Track trips end "Landing" then "Enroute" at 34 to 42 kt at the strip) unless 500 ft or more above the Landing,
 * when the aircraft is flying again; V2Track's "Enroute" is flying, except, when the device sent no Landing word
 * (VH-JEI's trip of 19 Oct 2026 ends "Enroute" at 44 kt at the strip's level), under 1.5 times the kind's airborne
 * speed within 150 ft of the trip's last known ground level (its latest ground or "Take Off" point).
 */
export function flyingFromStatus(
  points: readonly TrackPoint[],
  atMs: number,
  kind: AircraftKind,
): boolean | null {
  const before = points
    .filter((p) => p.timeMs <= atMs && statusWord(p) !== '')
    .sort((a, b) => a.timeMs - b.timeMs);
  const last = before[before.length - 1];
  if (!last || atMs - last.timeMs > STATUS_MAX_AGE_MS) return null;
  const w = statusWord(last);
  if (GROUND_WORDS.has(w)) return false;
  if (TAKE_OFF_WORDS.has(w)) return true;
  let landing: TrackPoint | null = null;
  let ground: TrackPoint | null = null;
  for (const p of before) {
    const pw = statusWord(p);
    if (TAKE_OFF_WORDS.has(pw)) {
      landing = null;
      ground = p;
    } else if (pw === 'landing') {
      landing = p;
      ground = p;
    } else if (GROUND_WORDS.has(pw)) ground = p;
  }
  if (landing) {
    // landed and not taken off since: the landing roll, unless it has climbed away again
    return last.altFt !== null && landing.altFt !== null && last.altFt >= landing.altFt + CLIMBED_FT;
  }
  if (w !== 'enroute') return null;
  if (
    ground &&
    last.altFt !== null &&
    ground.altFt !== null &&
    (last.speedKt ?? 0) < LANDING_ROLL_SPEED_FACTOR * AIRBORNE_KT[kind] &&
    Math.abs(last.altFt - ground.altFt) <= GROUND_LEVEL_FT
  )
    return false;
  return true;
}

// ------------------------------------------------------------------------------------------- landing evidence

/** A fix this close to a known aerodrome (OurAirports' Australian list, `au-aerodromes.json`) is at it. */
export const LANDING_AERODROME_KM = 3;
/** How far back the approach is looked for before a slow fix at an aerodrome. */
export const LANDING_DESCENT_WINDOW_MS = 10 * 60_000;
/** The aircraft must have been at least this far above the fix, and faster, inside that window: it came down. */
export const LANDING_DESCENT_FT = 300;

/** Distance in kilometres between two points on the globe. */
function kmBetween(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180;
  const h =
    Math.sin(((b.lat - a.lat) * r) / 2) ** 2 +
    Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2;
  return 2 * 6371.0088 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The known aerodrome within `LANDING_AERODROME_KM` of a point, the nearest first; null when none. */
export function aerodromeNear(p: {
  lat: number;
  lon: number;
}): { code: string; name: string; km: number } | null {
  let best: { code: string; name: string; km: number } | null = null;
  // a quick bound first: 3 km is under 0.03° of latitude, and of longitude everywhere in Australia
  const span = 0.05;
  for (const a of auAerodromeList()) {
    if (Math.abs(a.lat - p.lat) > span || Math.abs(a.lon - p.lon) > span) continue;
    const km = kmBetween(a, p);
    if (km <= LANDING_AERODROME_KM && (!best || km < best.km)) best = { code: a.code, name: a.name, km };
  }
  return best;
}

// ----------------------------------------------------------------------- the flight's own strip (decision 949)

/** A strip of a project's flight plan (its airport or alternate pin): a landing place of the evidence rule. */
export interface LandingStrip {
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
}

/** Where a slow fix after a descent is taken as a landing: the kind of place, and how far it is. */
export type LandingPlace =
  | { readonly kind: 'aerodrome'; readonly code: string; readonly name: string; readonly km: number }
  | { readonly kind: 'start_up' | 'take_off'; readonly km: number }
  | { readonly kind: 'strip'; readonly name: string; readonly km: number };

/** Words that say the aircraft was started on the ground, where it was then. */
const START_UP_WORDS: ReadonlySet<string> = new Set(['start up', 'startup', 'engine on', 'tracker power up']);
/** Under the airborne speed for this long before a fast fix, the aircraft was on the ground, not in a speed dip. */
const GROUND_STOP_MS = 2 * 60_000;

/**
 * Where the flight the fix of `atMs` belongs to began (decision 949, MB: "most survey flights take off and land back
 * at the same strip or airfield"): its start-up fix (the latest "Start Up", "Engine On" or "Tracker Power Up" word
 * while it was on the ground before the take-off) and its take-off fix (its "Take Off" word, else the last fix on
 * the ground before the speed crossed the kind's airborne speed, the ground being a ground word, the trail's start,
 * or under that speed for 2 minutes or more, so a speed dip in flight is not a take-off; the same crossing
 * `flightStatus` times). The latest take-off at or before `atMs` counts, so a flight after a landing has its own.
 * After a "Landing" word, a fast report under 500 ft above it is the landing roll, not a take-off. Empty when the
 * flight was first seen airborne (nothing is known of where it began) and for a ground vehicle.
 */
export function flightStartPlaces(
  points: readonly TrackPoint[],
  atMs: number,
  kind: AircraftKind,
): TrackPoint[] {
  const threshold = AIRBORNE_KT[kind];
  if (!Number.isFinite(threshold)) return [];
  const pts = points
    .filter((p) => p.timeMs <= atMs && (p.speedKt !== null || statusWord(p) !== ''))
    .sort((a, b) => a.timeMs - b.timeMs);
  let state: 'unknown' | 'ground' | 'airborne' = 'unknown';
  /** The ground period the aircraft is in: when it began, whether a word or the trail's start vouches for it. */
  let groundSince = 0;
  let groundSure = false;
  let groundFix: TrackPoint | null = null;
  let startUp: TrackPoint | null = null;
  let landingAlt: number | null | undefined;
  let flightStartUp: TrackPoint | null = null;
  let takeOff: TrackPoint | null = null;
  for (const p of pts) {
    const w = statusWord(p);
    if (TAKE_OFF_WORDS.has(w)) {
      takeOff = p;
      flightStartUp = state === 'ground' ? startUp : null;
      state = 'airborne';
      continue;
    }
    const groundWord = GROUND_WORDS.has(w);
    if (!groundWord && p.speedKt === null) continue;
    if (!groundWord && p.speedKt! >= threshold) {
      if (state === 'ground') {
        const rolling =
          landingAlt !== undefined &&
          !(landingAlt !== null && p.altFt !== null && p.altFt >= landingAlt + CLIMBED_FT);
        if (rolling) continue;
        if (groundSure || p.timeMs - groundSince >= GROUND_STOP_MS) {
          takeOff = groundFix ?? p;
          flightStartUp = startUp;
        }
      }
      state = 'airborne';
      continue;
    }
    // on the ground: a ground word, or under the airborne speed
    if (state !== 'ground') {
      groundSince = p.timeMs;
      groundSure = groundWord || state === 'unknown';
      startUp = null;
      landingAlt = undefined;
    } else if (groundWord) groundSure = true;
    state = 'ground';
    groundFix = p;
    if (START_UP_WORDS.has(w)) startUp = p;
    if (w === 'landing') landingAlt = p.altFt;
  }
  if (!takeOff) return [];
  return flightStartUp && flightStartUp !== takeOff ? [flightStartUp, takeOff] : [takeOff];
}

/**
 * The strips of a tenant's flight plans (`flight_plan.request.airport` and `.alternate`, each a pin `{name,
 * position: {lat, lng}}`, or a bare `{lat, lng}` in the planner's own requests), newest version first, each
 * position once (within about 10 m). Rows with no usable position are skipped. The same reading in the cloud poller
 * (`PollInput.strips`) and Teslyn Field (the open project's latest plan).
 */
export function stripsFromPlans(
  rows: readonly {
    readonly project_id: string;
    readonly version: number;
    readonly airport: unknown;
    readonly alternate: unknown;
  }[],
): LandingStrip[] {
  const pin = (raw: unknown, fallback: string): LandingStrip | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as { name?: unknown; position?: unknown; lat?: unknown; lng?: unknown };
    const at = (r.position && typeof r.position === 'object' ? r.position : r) as {
      lat?: unknown;
      lng?: unknown;
    };
    const lat = typeof at.lat === 'number' ? at.lat : NaN;
    const lon = typeof at.lng === 'number' ? at.lng : NaN;
    if (!validFix({ lat, lon, timeMs: 1 })) return null;
    const name = typeof r.name === 'string' && r.name.trim() ? r.name.trim() : fallback;
    return { name, lat, lon };
  };
  const out: LandingStrip[] = [];
  const seen = new Set<string>();
  for (const row of [...rows].sort((a, b) => b.version - a.version)) {
    for (const s of [pin(row.airport, 'Airport'), pin(row.alternate, 'Alternate')]) {
      if (!s) continue;
      const key = `${s.lat.toFixed(4)},${s.lon.toFixed(4)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
  }
  return out;
}

/**
 * The landing place of the fix at `atMs`, if it is at one: a known aerodrome (`aerodromeNear`, 3 km); else the
 * flight's own start-up or take-off fix (`flightStartPlaces`) within 3 km and, when both have an altitude, within
 * 150 ft of its altitude (a helicopter slow over its strip at height is not landing there); else a strip of the
 * project's flight plan within 3 km (no elevation, as the aerodrome list: the descent stands in). Null when none.
 */
export function landingPlaceNear(
  points: readonly TrackPoint[],
  atMs: number,
  kind: AircraftKind,
  strips: readonly LandingStrip[],
): LandingPlace | null {
  const fix = points.filter((p) => p.timeMs <= atMs).sort((a, b) => b.timeMs - a.timeMs)[0];
  if (!fix) return null;
  const aerodrome = aerodromeNear(fix);
  if (aerodrome) return { kind: 'aerodrome', ...aerodrome };
  for (const place of flightStartPlaces(points, atMs, kind)) {
    const km = kmBetween(place, fix);
    if (km > LANDING_AERODROME_KM) continue;
    if (place.altFt !== null && fix.altFt !== null && Math.abs(fix.altFt - place.altFt) > GROUND_LEVEL_FT)
      continue;
    return { kind: START_UP_WORDS.has(statusWord(place)) ? 'start_up' : 'take_off', km };
  }
  for (const s of strips) {
    const km = kmBetween(s, fix);
    if (km <= LANDING_AERODROME_KM) return { kind: 'strip', name: s.name, km };
  }
  return null;
}

/**
 * Landing evidence for a fix whose tracker gave no word (a feed that stopped during the landing roll, a provider
 * without words): true when the fix is slow (under 1.5 times the kind's airborne speed: 60 kt fixed-wing, 30 kt
 * helicopter), at a landing place (`landingPlaceNear`: a known aerodrome within 3 km, the flight's own start-up or
 * take-off position at its level, or a strip of the project's flight plan; decision 949), and the aircraft came
 * down to it (a fix inside the 10 minutes before at least 300 ft higher and faster). VH-JEI's TracPlus feed of
 * 20 Oct 2026 ends at Batchelor 43 kt, 360 ft, from 754 ft and 91 kt a minute before; MB's alert of 2 Nov 2026
 * ended the same way (45 kt, 360 ft, 06:35:17); P2-PHU's Spider Tracks flight of 9 Oct 2026 comes back over its
 * Engine On point at Goroka at 22 kt, 5292 ft, from 5729 ft and 74 kt two minutes before. False otherwise: a low,
 * slow aircraft away from every place, or one that did not descend to it, is still flying for all the trail says,
 * so a silence after it is lost tracking. Never true without an altitude or a speed.
 */
export function landedByEvidence(
  points: readonly TrackPoint[],
  atMs: number,
  kind: AircraftKind,
  strips: readonly LandingStrip[] = [],
): boolean {
  const fix = points.filter((p) => p.timeMs <= atMs).sort((a, b) => b.timeMs - a.timeMs)[0];
  if (!fix || fix.speedKt === null || fix.altFt === null) return false;
  if (!Number.isFinite(AIRBORNE_KT[kind])) return false;
  if (fix.speedKt >= LANDING_ROLL_SPEED_FACTOR * AIRBORNE_KT[kind]) return false;
  if (!landingPlaceNear(points, atMs, kind, strips)) return false;
  return points.some(
    (p) =>
      p.timeMs < fix.timeMs &&
      fix.timeMs - p.timeMs <= LANDING_DESCENT_WINDOW_MS &&
      p.altFt !== null &&
      p.speedKt !== null &&
      p.altFt >= fix.altFt! + LANDING_DESCENT_FT &&
      p.speedKt > fix.speedKt!,
  );
}

/**
 * Whether the aircraft was flying at the fix of `atMs`, from its trail: the tracker's own words first
 * (`flyingFromStatus`), then the landing evidence (`landedByEvidence`, with the project's flight-plan strips: false),
 * else null, when the speed decides (`isAirborne`). One rule for every provider, in Teslyn Field
 * (`tracker-feed.ts`) and the cloud poller (`tracker-poll.ts`), so the alert, the map and the stored fix agree.
 */
export function flyingFromTrail(
  points: readonly TrackPoint[],
  atMs: number,
  kind: AircraftKind,
  strips: readonly LandingStrip[] = [],
): boolean | null {
  const words = flyingFromStatus(points, atMs, kind);
  if (words !== null) return words;
  return landedByEvidence(points, atMs, kind, strips) ? false : null;
}

/**
 * A position judged by its trail at the fix's time (`flyingFromTrail`: the tracker's status words, then the landing
 * evidence) when the provider gave no flying flag of its own; the position itself when the trail says nothing.
 */
export function withStatus(
  p: TrackedPosition,
  trail: readonly TrackPoint[],
  kind: AircraftKind,
  strips: readonly LandingStrip[] = [],
): TrackedPosition {
  if (p.providerFlying !== null || trail.length === 0) return p;
  const flying = flyingFromTrail(trail, p.timeMs, kind, strips);
  return flying === null ? p : { ...p, providerFlying: flying };
}

/**
 * The state of an aircraft now: SOS first; then lost tracking (airborne at its last fix and no fix for the tenant's
 * lost-tracking minutes); stale on the ground; airborne; parked.
 */
export function trackerState(
  p: TrackedPosition,
  kind: AircraftKind,
  nowMs: number,
  settings: TrackingSettings,
): TrackerState {
  const ageMin = (nowMs - p.timeMs) / 60_000;
  const airborne = isAirborne(p, kind);
  if (isPublicTraffic(p.source)) {
    if (ageMin >= PUBLIC_STALE_MINUTES) return 'stale';
    return airborne ? 'airborne' : 'parked';
  }
  if (p.sos) return 'sos';
  if (ageMin >= lostTrackingMinutes(settings, p.source)) return airborne ? 'lost' : 'stale';
  return airborne ? 'airborne' : 'parked';
}

export const TRACKER_STATE_WORD: Readonly<Record<TrackerState, string>> = {
  sos: 'SOS',
  lost: 'Lost tracking',
  stale: 'No recent position',
  airborne: 'Airborne',
  parked: 'On the ground',
};

/** "12 min ago", "2.5 h ago". */
export function ageText(nowMs: number, timeMs: number): string {
  const s = Math.max(0, (nowMs - timeMs) / 1000);
  if (s < 60) return `${Math.round(s)} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${(s / 3600).toFixed(1)} h ago`;
}

export interface LostTrackingAlert {
  readonly id: string;
  readonly label: string;
  readonly kind: 'lost' | 'resumed' | 'sos';
  readonly minutes: number;
}

/**
 * Alerts raised once: "lost" when an aircraft enters lost tracking, "resumed" when it reports again, "sos" when SOS
 * starts (inventory 03 §9 acceptance: one alert at N minutes and one resumed notice). `active` is the set returned by
 * the previous call.
 */
export function trackingAlerts(
  previous: ReadonlySet<string>,
  aircraft: readonly {
    readonly id: string;
    readonly label: string;
    readonly state: TrackerState;
    readonly ageMin: number;
  }[],
): { readonly alerts: LostTrackingAlert[]; readonly active: Set<string> } {
  const active = new Set<string>();
  const alerts: LostTrackingAlert[] = [];
  for (const a of aircraft) {
    if (a.state === 'lost' || a.state === 'sos') {
      const key = `${a.state}:${a.id}`;
      active.add(key);
      if (!previous.has(key))
        alerts.push({ id: a.id, label: a.label, kind: a.state, minutes: Math.round(a.ageMin) });
    } else if (previous.has(`lost:${a.id}`) && a.state !== 'stale') {
      alerts.push({ id: a.id, label: a.label, kind: 'resumed', minutes: 0 });
    }
  }
  return { alerts, active };
}

/**
 * A lost-tracking, SOS or resumed alert queued for the phone relay (Diurnal parity 03-037; the tool's
 * `_companionStaleAlerts`, am:6660–6676, 7725–7737). The relay that sends it to field crew and project managers
 * (decision 53) is phase 5 (L17); until then Field keeps the record (`tracking_alert`). One record per aircraft and
 * episode: the id holds the time of the fix the aircraft went quiet after (or reported again with), so every map
 * window, a restart and a second look at the same episode all give the same id and the queue keeps one.
 */
export interface QueuedTrackingAlert {
  /** `<kind>:<aircraft map id>:<fix time ms>`. */
  readonly id: string;
  readonly aircraft_key: string;
  readonly label: string;
  readonly kind: 'lost' | 'sos' | 'resumed';
  /** Minutes since the fix when raised (0 for resumed). */
  readonly minutes: number;
  readonly last_fix_ms: number;
  readonly raised_at_ms: number;
}

/**
 * An episode that started longer ago than this before a laptop noticed it is not queued: an aircraft that went quiet
 * hours ago (a V2Track device last seen airborne last week) is not news to a phone. Two hours (decision 445, Q377; was
 * 12 h). Alerts are for the organisation's own trackers only, never public aircraft (the feed passes only its fleet).
 */
export const ALERT_QUEUE_MAX_AGE_MS = 2 * 3_600_000;

/** The alerts of `trackingAlerts` as queue records, with the fix time of each aircraft. */
export function alertsToQueue(
  alerts: readonly LostTrackingAlert[],
  fixMs: (id: string) => number | undefined,
  nowMs: number,
): QueuedTrackingAlert[] {
  const out: QueuedTrackingAlert[] = [];
  for (const a of alerts) {
    const t = fixMs(a.id);
    if (t === undefined || !Number.isFinite(t) || nowMs - t > ALERT_QUEUE_MAX_AGE_MS) continue;
    out.push({
      id: `${a.kind}:${a.id}:${t}`,
      aircraft_key: a.id,
      label: a.label,
      kind: a.kind,
      minutes: a.minutes,
      last_fix_ms: t,
      raised_at_ms: nowMs,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- trails

/** Merge new points into a trail: time order, one point per time, older than the retention dropped, capped. */
export function mergeTrail(
  trail: readonly TrackPoint[],
  points: readonly TrackPoint[],
  nowMs: number,
): TrackPoint[] {
  const cutoff = nowMs - TRAIL_RETENTION_MS;
  const byTime = new Map<number, TrackPoint>();
  for (const p of [...trail, ...points]) {
    if (p.timeMs >= cutoff && p.timeMs <= nowMs + 300_000 && validFix(p)) byTime.set(p.timeMs, p);
  }
  const out = [...byTime.values()].sort((a, b) => a.timeMs - b.timeMs);
  return out.length > TRAIL_MAX_POINTS ? out.slice(out.length - TRAIL_MAX_POINTS) : out;
}

/** The part of a trail inside the chosen history depth (hours, at most the retention). */
export function trailWithin(trail: readonly TrackPoint[], hours: number, nowMs: number): TrackPoint[] {
  const h = Math.min(Math.max(hours, 0), TRAIL_RETENTION_HOURS);
  const cutoff = nowMs - h * 3_600_000;
  return trail.filter((p) => p.timeMs >= cutoff);
}

/**
 * Every trail cut to the history depth the map shows (03-028): points older than `hours` (at most the 48 hours
 * kept) are left out; a point without a time (a TracPlus path) is kept.
 */
export function trailsWithin<P extends { readonly timeMs?: number }>(
  trails: ReadonlyMap<string, readonly P[]>,
  hours: number,
  nowMs: number,
): Map<string, P[]> {
  const cutoff = nowMs - Math.min(Math.max(hours, 0), TRAIL_RETENTION_HOURS) * 3_600_000;
  const out = new Map<string, P[]>();
  for (const [id, t] of trails)
    out.set(
      id,
      t.filter((p) => p.timeMs === undefined || p.timeMs >= cutoff),
    );
  return out;
}

/** A usable fix: finite, on the globe, not 0,0 (v2track_api.rs:283–346). */
export function validFix(p: Pick<TrackPoint, 'lat' | 'lon' | 'timeMs'>): boolean {
  return (
    Number.isFinite(p.lat) &&
    Number.isFinite(p.lon) &&
    Number.isFinite(p.timeMs) &&
    p.timeMs > 0 &&
    Math.abs(p.lat) <= 90 &&
    Math.abs(p.lon) <= 180 &&
    !(p.lat === 0 && p.lon === 0)
  );
}

// ---------------------------------------------------------------------------------------------- registrations

/** "VH-ABC", "vhabc", "ABC" (a three-letter Australian short rego) → "VH-ABC"; other countries kept as typed. */
export function normaliseRegistration(raw: string | null | undefined): string | null {
  const s = (raw ?? '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (!s) return null;
  const vh = /^VH-?([A-Z0-9]{2,4})$/.exec(s);
  if (vh) return `VH-${vh[1]}`;
  if (/^[A-Z]{3}$/.test(s)) return `VH-${s}`;
  return s;
}

/** Six hex digits, lower case, or null. */
export function normaliseHex(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim().toLowerCase().replace(/^~/, '');
  return /^[0-9a-f]{6}$/.test(s) ? s : null;
}

/** The key one aircraft has on every source: its registered asset, else its registration, else source and device. */
export function aircraftKey(p: TrackedPosition, asset: TrackerAsset | null): string {
  if (asset) return `asset:${asset.id}`;
  const rego = normaliseRegistration(p.registration ?? p.label);
  if (rego && /^VH-[A-Z0-9]{2,4}$/.test(rego)) return `rego:${rego}`;
  const hex = normaliseHex(p.icaoHex);
  if (hex) return `hex:${hex}`;
  return `${p.source}:${p.account}:${p.deviceId}`;
}

/** Fleet trackers above public traffic (inventory 03 §6, fixing the Diurnal table that left out two fleet sources). */
export const DEFAULT_SOURCE_PRIORITY: readonly TrackerSourceId[] = [
  'v2track',
  'spidertracks',
  'websentinel',
  'tracplus',
  'ozrunways',
  'avplan',
  'adsb',
  'opensky',
];
/**
 * bushfire.io (decision 476) is not in the order a tenant sees and sets: it is off for every organisation until
 * Disaster Science answers, and two minutes behind live, so a source the order does not name ranks last and every
 * other feed of the same aircraft wins while fresh.
 */
const LEGEND_ORDER: readonly TrackerSourceId[] = [...DEFAULT_SOURCE_PRIORITY, 'bushfireio'];

/** The asset a position belongs to: same provider, account and device id. */
export function assetFor(p: TrackedPosition, assets: readonly TrackerAsset[]): TrackerAsset | null {
  return (
    assets.find(
      (a) =>
        !a.deleted_at &&
        a.provider === p.source &&
        a.account_label === p.account &&
        a.device_id === p.deviceId,
    ) ?? null
  );
}

/** One real aircraft on the map: its best position, its asset, and every position that is the same aircraft. */
export interface AircraftGroup {
  readonly key: string;
  readonly position: TrackedPosition;
  readonly asset: TrackerAsset | null;
  /** Every position of the aircraft (the shown one included), from every source. */
  readonly members: readonly TrackedPosition[];
}

/** The identities a position carries: its own asset, a VH registration, a hex (with an asset's, when it has one). */
function identities(p: TrackedPosition, own: TrackerAsset | null): string[] {
  const out: string[] = [];
  if (own) out.push(`asset:${own.id}`);
  const rego = normaliseRegistration(own?.registration || p.registration || p.label);
  if (rego && /^VH-[A-Z0-9]{2,4}$/.test(rego)) out.push(`rego:${rego}`);
  const hex = normaliseHex(own?.icao_hex || p.icaoHex);
  if (hex) out.push(`hex:${hex}`);
  return out;
}

const nameKey = (s: string | null | undefined): string => (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
/** A position's names to match bushfire.io's by: its callsign (label) and registration, without spaces or hyphens. */
const namesOf = (p: TrackedPosition): string[] =>
  [...new Set([nameKey(p.label), nameKey(p.registration)])].filter((n) => n.length >= 3);
/** bushfire.io's names are matched within this distance (its positions trail live by about two minutes). */
const BUSHFIRE_MATCH_KM = 50;

/** Where bushfire.io saw each of its names (decision 476). */
function bushfireNames(positions: readonly TrackedPosition[]): Map<string, TrackedPosition[]> {
  const out = new Map<string, TrackedPosition[]>();
  for (const p of positions)
    if (p.source === 'bushfireio') for (const n of namesOf(p)) out.set(n, [...(out.get(n) ?? []), p]);
  return out;
}

/**
 * bushfire.io's aircraft carry no ICAO hex, so they join the other feeds by name, as MB's app does: its transponder
 * callsign or registration (foreign ones too, "N138CG") agreeing with another feed's within 50 km.
 */
function bushfireIdentities(
  p: TrackedPosition,
  names: ReadonlyMap<string, readonly TrackedPosition[]>,
): string[] {
  if (names.size === 0) return [];
  return namesOf(p)
    .filter((n) =>
      p.source === 'bushfireio'
        ? true
        : (names.get(n) ?? []).some((b) => kmBetween(b, p) <= BUSHFIRE_MATCH_KM),
    )
    .map((n) => `fire:${n}`);
}

/**
 * One position per real aircraft (inventory 03 §6 acceptance; issue #55). Positions are grouped by every identity
 * they share: their own tracker asset (same provider, account and device id), a tenant asset's registration or hex,
 * a VH registration, a hex (an ADS-B fix carrying both a hex and a registration links the two). In each group the
 * best source wins by the tenant's priority, a fresher fix breaking a tie, and a fix more than the lost-tracking
 * minutes older than the newest losing to it. The group's asset is the one of the highest-priority tracker (then
 * the lowest id), so the aircraft keeps one id whichever source is shown. A hidden asset hides its aircraft.
 */
export function onePerAircraft(
  positions: readonly TrackedPosition[],
  assets: readonly TrackerAsset[],
  settings: TrackingSettings,
): AircraftGroup[] {
  const order = settings.source_priority.length > 0 ? settings.source_priority : DEFAULT_SOURCE_PRIORITY;
  const rank = (s: TrackerSourceId): number => {
    const i = order.indexOf(s);
    return i < 0 ? order.length : i;
  };
  // union-find over identities: assets join their registration and hex, positions join everything they carry
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = x;
    while (parent.has(r) && parent.get(r) !== r) r = parent.get(r)!;
    parent.set(x, r);
    return r;
  };
  const join = (ids: readonly string[]): void => {
    if (ids.length === 0) return;
    const root = find(ids[0]!);
    for (const id of ids.slice(1)) {
      const r = find(id);
      if (r !== root) parent.set(r, root);
    }
  };
  const live = assets.filter((a) => !a.deleted_at);
  const assetById = new Map(live.map((a) => [`asset:${a.id}`, a]));
  for (const a of live) {
    const rego = normaliseRegistration(a.registration);
    const hex = normaliseHex(a.icao_hex);
    join([
      `asset:${a.id}`,
      ...(rego && /^VH-[A-Z0-9]{2,4}$/.test(rego) ? [`rego:${rego}`] : []),
      ...(hex ? [`hex:${hex}`] : []),
    ]);
  }
  const usable = positions.filter((p) => validFix(p));
  const fireNames = bushfireNames(usable);
  const ids = usable.map((p) => {
    const own = assetFor(p, live);
    const list = [...identities(p, own), ...bushfireIdentities(p, fireNames)];
    return list.length > 0 ? list : [`${p.source}:${p.account}:${p.deviceId}`];
  });
  ids.forEach(join);
  const groups = new Map<string, { members: TrackedPosition[]; ids: Set<string> }>();
  usable.forEach((p, i) => {
    const root = find(ids[i]![0]!);
    const g = groups.get(root) ?? { members: [], ids: new Set<string>() };
    g.members.push(p);
    for (const id of ids[i]!) g.ids.add(id);
    groups.set(root, g);
  });
  // every identity of a component, assets included (an asset joined only through its registration or hex)
  const componentIds = new Map<string, Set<string>>();
  for (const id of parent.keys()) {
    const r = find(id);
    const set = componentIds.get(r) ?? new Set<string>();
    set.add(id);
    componentIds.set(r, set);
  }
  const out: AircraftGroup[] = [];
  for (const [root, g] of groups) {
    const all = componentIds.get(root) ?? g.ids;
    const groupAssets = [...all]
      .map((id) => assetById.get(id))
      .filter((a): a is TrackerAsset => a !== undefined)
      .sort((a, b) => rank(a.provider) - rank(b.provider) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const asset = groupAssets[0] ?? null;
    // a position whose own asset is hidden is left out; a hidden group asset hides the whole aircraft
    if (asset?.hidden) continue;
    const members = g.members.filter((p) => !assetFor(p, live)?.hidden);
    let best: TrackedPosition | null = null;
    for (const p of members) {
      if (!best) {
        best = p;
        continue;
      }
      const staleGap = lostTrackingMinutes(settings, best.source) * 60_000;
      const better =
        p.timeMs - best.timeMs > staleGap ||
        (best.timeMs - p.timeMs <= staleGap &&
          (rank(p.source) < rank(best.source) ||
            (rank(p.source) === rank(best.source) && p.timeMs > best.timeMs)));
      if (better) best = p;
    }
    if (!best) continue;
    const key = asset
      ? `asset:${asset.id}`
      : ([...all].filter((id) => id.startsWith('rego:')).sort()[0] ??
        [...all].filter((id) => id.startsWith('hex:')).sort()[0] ??
        aircraftKey(best, null));
    out.push({ key, position: best, asset, members });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- RFDS (Q46)

/** Royal Flying Doctor callsigns: FD then digits, optionally a letter (am:30–33). */
export const RFDS_CALLSIGN = /^FD\d+[A-Z]?$/;

/** A tenant's RFDS identity (synced table `rfds_identity`). */
export interface RfdsIdentity {
  readonly id: string;
  readonly icao_hex: string;
  readonly registration: string;
  readonly callsign: string;
  readonly deleted_at: string | null;
}

/** Whether a position is an RFDS aircraft: its callsign, or a hex or registration the tenant has confirmed. */
export function isRfds(
  p: Pick<TrackedPosition, 'label' | 'icaoHex' | 'registration'>,
  registry: readonly RfdsIdentity[],
): boolean {
  if (RFDS_CALLSIGN.test(p.label.toUpperCase().replace(/\s/g, ''))) return true;
  const hex = normaliseHex(p.icaoHex);
  const rego = normaliseRegistration(p.registration);
  return registry.some(
    (r) =>
      !r.deleted_at && ((hex !== null && r.icao_hex === hex) || (rego !== null && r.registration === rego)),
  );
}

/**
 * A new identity to remember (and sync to the tenant) when an FD callsign is seen with a hex or registration that
 * the tenant's registry does not hold yet; null otherwise (inventory 03 §2.10).
 */
export function rfdsObservation(
  p: Pick<TrackedPosition, 'label' | 'icaoHex' | 'registration'>,
  registry: readonly RfdsIdentity[],
): { readonly icao_hex: string; readonly registration: string; readonly callsign: string } | null {
  const callsign = p.label.toUpperCase().replace(/\s/g, '');
  if (!RFDS_CALLSIGN.test(callsign)) return null;
  const hex = normaliseHex(p.icaoHex) ?? '';
  const rego = normaliseRegistration(p.registration) ?? '';
  if (!hex && !rego) return null;
  const known = registry.some(
    (r) => !r.deleted_at && ((hex && r.icao_hex === hex) || (rego && r.registration === rego)),
  );
  return known ? null : { icao_hex: hex, registration: rego, callsign };
}

// ---------------------------------------------------------------------------------------------- kinds (03-021)

/**
 * Helicopter words and models in a type text (am:3440–3447), without "Kolb", a fixed-wing ultralight maker the
 * Diurnal list wrongly held (bug 03-12), and without bare "safari", which misfires on labels.
 */
const HELICOPTER_TYPE =
  /\b(helicopters?|heli|rotorcraft|rotary|robinson|r22|r44|r66|bell\s?\d{3}[a-z]?|as\s?3[45]\d|ec\s?1[23]\d|h1[2-6]\d|eurocopter|sikorsky|s-?76|agusta|aw\s?1\d\d|enstrom|schweizer|guimbal|cabri|kaman|hiller|brantly|md\s?[5-9]\d\d|hughes\s?369|mi-\d+|ka-\d+)\b/i;

/** Helicopter when a type text names a helicopter type or maker; null when it does not say. */
export function kindFromTypeText(typeText: string | null | undefined): AircraftKind | null {
  return typeText && HELICOPTER_TYPE.test(typeText) ? 'helicopter' : null;
}

/**
 * The tracker kind of a register's airframe class (aircraft details): rotorcraft and gyroplanes fly slow, as
 * helicopters (airborne from 20 knots); every other aircraft is fixed-wing; a drone or unclassified airframe says
 * nothing.
 */
export function kindFromRegisterClass(c: AirframeClass | null | undefined): AircraftKind | null {
  if (!c) return null;
  if (c === 'helicopter' || c === 'gyroplane') return 'helicopter';
  if (c === 'drone' || c === 'other') return null;
  return 'fixed_wing';
}

/**
 * ICAO type designators of helicopters (the Diurnal tool's `HELI_ICAO_CODES`, am:3427–3437), with B407 (Bell 407),
 * which the tool's list lacked although Community ADS-B reports VH-EXY as one (genuine capture of 20 Oct 2026).
 */
// prettier-ignore
export const HELICOPTER_ICAO_TYPES: ReadonlySet<string> = new Set([
  'AS50', 'AS55', 'AS65', 'EC20', 'EC25', 'EC30', 'EC35', 'EC45', 'EC55', 'EC75',
  'H160', 'H175', 'EC60', 'EC65', 'A109', 'A119', 'A139', 'A149', 'A169', 'A189',
  'B06', 'B105', 'B206', 'B212', 'B214', 'B222', 'B230', 'B407', 'B412', 'B429', 'B430', 'B47G', 'B47J',
  'BK17', 'BO05', 'CH47', 'CH53', 'CH54', 'COLI', 'COMU', 'EN28', 'EN48',
  'EXEC', 'EX35', 'G2CA', 'GAZE', 'HUCO', 'K126', 'K1200',
  'LAMA', 'LYNX', 'MD52', 'MD60', 'MI2', 'MI6', 'MI8', 'MI17', 'MI24', 'MI26', 'MI28', 'MI34', 'MI35', 'MI38',
  'NH90', 'OH6', 'R22', 'R44', 'R66', 'R77', 'S300', 'S330', 'S333', 'S55', 'S58', 'S61', 'S64', 'S70', 'S76', 'S92',
  'S269', 'SA18', 'SA34', 'SA41', 'SA42', 'SA65', 'TIGR', 'UH1', 'UH60', 'V500', 'W3', 'WASP', 'WG30',
  'PUMA', 'MERL', 'COUG', 'DAUP', 'PANT', 'FENX', 'SKOR',
]);

/**
 * The kind of a public aircraft from its ICAO type designator, else its ADS-B emitter category: a helicopter type
 * is a helicopter; an aircraft with no type and category A7 (rotorcraft) is a helicopter. A known type that is not a
 * helicopter is not one, whatever the category says (VH-BLM, a CL60, reports A7 in the genuine capture). Null when
 * nothing says (fixed-wing by default).
 */
export function kindFromIcaoType(
  type: string | null | undefined,
  category?: string | null,
): AircraftKind | null {
  const t = (type ?? '').trim().toUpperCase();
  if (t) return HELICOPTER_ICAO_TYPES.has(t) ? 'helicopter' : null;
  return (category ?? '').trim().toUpperCase() === 'A7' ? 'helicopter' : null;
}

/**
 * The kind a tracker device most likely is when its provider does not say, from its label and fleet group, as the
 * Diurnal tool's V2Track patterns did (am:6681–6683, with the brand default "FieldCrew" for ground vehicles): a
 * field crew or vehicle word gives a ground vehicle (ground beats helicopter), a helicopter word or type a
 * helicopter; otherwise null (fixed-wing by default). A tenant's asset kind always wins.
 */
export function suggestTrackerKind(d: {
  readonly label: string;
  readonly fleetGroup: string;
  readonly typeText?: string;
  /** The national register's airframe class for its registration (aircraft details), when known: it wins. */
  readonly registerClass?: AirframeClass | null;
}): AircraftKind | null {
  const fromRegister = kindFromRegisterClass(d.registerClass ?? null);
  if (fromRegister) return fromRegister;
  const text = `${d.label} ${d.fleetGroup}`;
  if (/field\s*crew|\b(vehicles?|ute|4wd|truck)\b/i.test(text)) return 'ground';
  return kindFromTypeText(text) ?? kindFromTypeText(d.typeText);
}

// ---------------------------------------------------------------------------------------------- assets (03-020)

export const TRACKER_KIND_WORD: Readonly<Record<AircraftKind, string>> = {
  fixed_wing: 'Fixed-wing',
  helicopter: 'Helicopter',
  ground: 'Ground vehicle',
};

/**
 * A new tracker asset for a device a tracker reports (decision 69: the tracker's own list), named as the tracker
 * names it, with the kind its provider says or Teslyn suggests (fixed-wing when neither does).
 */
export function assetFromDevice(
  p: TrackedPosition,
  tenantId: string,
  registerClass: AirframeClass | null = null,
): TrackerAsset {
  if (!(FLEET_PROVIDERS as readonly string[]).includes(p.source))
    throw new Error(`${TRACKER_SOURCE_NAME[p.source]} is public traffic, not a tracker of the organisation.`);
  return {
    id: '',
    tenant_id: tenantId,
    provider: p.source as FleetProviderId,
    account_label: p.account,
    device_id: p.deviceId,
    label: p.label,
    registration: normaliseRegistration(p.registration ?? p.label) ?? '',
    icao_hex: normaliseHex(p.icaoHex) ?? '',
    kind:
      p.kind ??
      suggestTrackerKind({ label: p.label, fleetGroup: p.fleetGroup, registerClass }) ??
      'fixed_wing',
    colour: 'accent',
    fleet_group: p.fleetGroup,
    aircraft_id: null,
    hidden: false,
    deleted_at: null,
  };
}

/** What is wrong with an asset before it is saved, by field, in words; null when it can be saved. */
export function checkTrackerAsset(
  a: Pick<TrackerAsset, 'device_id' | 'label' | 'icao_hex' | 'registration'>,
): { readonly field: 'device_id' | 'label' | 'icao_hex' | 'registration'; readonly message: string } | null {
  if (!a.device_id.trim() || a.device_id.length > 128)
    return { field: 'device_id', message: 'Enter the tracker’s device id (up to 128 characters).' };
  if (!a.label.trim() || a.label.length > 60)
    return { field: 'label', message: 'Enter a name to show on the map.' };
  if (a.registration.length > 12)
    return { field: 'registration', message: 'Enter the registration in up to 12 characters.' };
  if (a.icao_hex.trim() && !/^[0-9a-f]{6}$/i.test(a.icao_hex.trim()))
    return {
      field: 'icao_hex',
      message: 'Enter the ICAO hex as six characters 0 to 9 and A to F, or leave it blank.',
    };
  return null;
}

/** An assignment window (Q47: what a project's client sees): open at either end, else ending after it starts. */
export function checkAssignmentWindow(startsAt: string | null, endsAt: string | null): string | null {
  const from = startsAt ? Date.parse(startsAt) : null;
  const to = endsAt ? Date.parse(endsAt) : null;
  if (from !== null && Number.isNaN(from)) return 'Enter the start as a date and time.';
  if (to !== null && Number.isNaN(to)) return 'Enter the end as a date and time.';
  if (from !== null && to !== null && to < from) return 'The assignment must end after it starts.';
  return null;
}

// ---------------------------------------------------------------------------------------------- fleet groups (03-004)

/** The fleet groups among the aircraft shown, sorted ('' is no group). */
export function fleetGroupsOf(aircraft: readonly { readonly group: string }[]): string[] {
  return [...new Set(aircraft.map((a) => a.group).filter((g) => g !== ''))].sort((a, b) =>
    a.localeCompare(b),
  );
}

/**
 * The aircraft of the ticked fleet groups (am:6569–6573, 7485–7521): none ticked shows every aircraft, and so does
 * a choice that matches no group any more; an aircraft without a group is always shown.
 */
export function showFleetGroups<T extends { readonly group: string }>(
  aircraft: readonly T[],
  shown: readonly string[],
): T[] {
  const on = new Set(shown);
  if (on.size === 0 || !aircraft.some((a) => on.has(a.group))) return [...aircraft];
  return aircraft.filter((a) => a.group === '' || on.has(a.group));
}

// ---------------------------------------------------------------------------------------------- the map

/** A position as the map draws it (`aircraft-layer.ts`), with its state and RFDS marking. */
export function toAircraftPosition(
  p: TrackedPosition,
  asset: TrackerAsset | null,
  nowMs: number,
  settings: TrackingSettings,
  rfds: boolean,
): AircraftPosition {
  const kind: AircraftKind = asset?.kind ?? p.kind ?? 'fixed_wing';
  const state = trackerState(p, kind, nowMs, settings);
  return {
    id: asset ? `asset:${asset.id}` : `${p.source}:${p.account}:${p.deviceId}`,
    label: asset?.label || normaliseRegistration(p.registration) || p.label,
    lat: p.lat,
    lng: p.lon,
    alt_ft: p.altFt,
    speed_kt: p.speedKt,
    heading_deg: p.headingDeg,
    time: new Date(p.timeMs).toISOString(),
    kind,
    source: TRACKER_SOURCE_NAME[p.source],
    group: asset?.fleet_group || p.fleetGroup,
    airborne: state === 'airborne' || state === 'lost' || (state === 'sos' && isAirborne(p, kind)),
    state,
    rfds,
    registration: asset?.registration || normaliseRegistration(p.registration),
    icaoHex: normaliseHex(asset?.icao_hex) ?? normaliseHex(p.icaoHex),
    satellite: p.satellite,
    fleet: !isPublicTraffic(p.source),
    ...(p.fire ? { fire: p.fire } : {}),
    // an ADS-B flight's callsign, when the label is its registration (the airline route, R160)
    ...((p.source === 'adsb' || p.source === 'opensky') && !asset && p.label ? { callsign: p.label } : {}),
  };
}

/**
 * The words beside an aircraft or vehicle on a map, as Teslyn Field's and Teslyn Cloud's: its label, its source and any
 * marking or state ("VH-ABC · AvPlan", "TA Fieldcrew 2 · V2Track · On the ground"), so SOS, lost tracking, RFDS and NAFC
 * are always in words, never colour alone. The flight line (take-off, flight time, landing) goes beside it on its own.
 */
export function trackedAircraftLabel(
  a: Pick<AircraftPosition, 'label' | 'source' | 'state' | 'rfds' | 'fire'>,
): string {
  const words = `${a.rfds ? ' · RFDS' : ''}${a.fire ? ' · Fire (NAFC)' : ''}${a.state ? ` · ${TRACKER_STATE_WORD[a.state]}` : ''}`;
  return `${a.label}${a.source ? ` · ${a.source}` : ''}${words}`;
}

export type AircraftGlyph = 'plane' | 'helicopter' | 'vehicle';

/**
 * How the map draws an aircraft (03-022, brand book Iconography): a plane, a helicopter or a vehicle seen from
 * above, nose up, turned to its heading (north when the tracker sends none); solid when airborne, outlined on the
 * ground; in the fail colour for SOS or lost tracking, always with the words beside it. An RFDS aircraft (the tenant's
 * marking, Q46) is drawn as a white aircraft on an upright aeromedical disc, always with the word "RFDS" (decision
 * 284; decision 320: not the Diurnal tool's red cross on white, a protected emblem under the Geneva Conventions Act
 * 1957 (Cth)). Its colour is `aircraftColours`'.
 */
export function aircraftGlyph(a: {
  readonly kind: AircraftKind;
  readonly heading_deg: number | null;
  readonly airborne: boolean;
  readonly state?: TrackerState;
  readonly rfds?: boolean;
}): {
  readonly glyph: AircraftGlyph;
  readonly rotationDeg: number;
  readonly filled: boolean;
  readonly alarm: boolean;
  readonly aeromedical: boolean;
} {
  const h = a.heading_deg !== null && Number.isFinite(a.heading_deg) ? a.heading_deg : 0;
  return {
    glyph: a.kind === 'helicopter' ? 'helicopter' : a.kind === 'ground' ? 'vehicle' : 'plane',
    rotationDeg: ((Math.round(h) % 360) + 360) % 360,
    filled: a.airborne,
    alarm: a.state === 'sos' || a.state === 'lost',
    aeromedical: a.rfds === true,
  };
}

const tones = (source: 'v2track' | 'spidertracks'): AircraftColour[] =>
  Array.from({ length: 16 }, (_, i) => `aircraft-${source}-${i + 1}` as AircraftColour);

/**
 * The Diurnal tool's colours per tracker source (decision 280, Q233; Diurnal parity 03-023), as tokens in
 * `brand/tokens` (`aircraft-*`): V2Track 16 blue tones (`V2TRACK_BLUE_PALETTE` 2195) and Spider Tracks 16 amber tones
 * (`ST_AMBER_PALETTE` 2211), one per aircraft; OpenSky orange (6087), Community ADS-B red (8191), Oz Runways and AvPlan
 * green (9671, 9106). The tool gave each TracPlus and WebSentinel aircraft a hue from its callsign (`stringToColor`
 * 1247), so neither had a source colour on the map; Teslyn takes the colour the tool marked each source with
 * elsewhere: TracPlus light blue (its source badge, 29136) and WebSentinel purple (its trail word, 36035).
 */
export const AIRCRAFT_SOURCE_PALETTES: Readonly<Record<TrackerSourceId, readonly AircraftColour[]>> = {
  v2track: tones('v2track'),
  spidertracks: tones('spidertracks'),
  websentinel: ['aircraft-websentinel'],
  tracplus: ['aircraft-tracplus'],
  opensky: ['aircraft-opensky'],
  adsb: ['aircraft-adsb'],
  ozrunways: ['aircraft-ozrunways'],
  avplan: ['aircraft-avplan'],
  // not a Diurnal source: the pink of MB's own app for bushfire.io (decision 476)
  bushfireio: ['aircraft-bushfireio'],
};

/** The words beside each source's colour in the map's legend (colour is never alone). */
export const AIRCRAFT_SOURCE_COLOUR_WORD: Readonly<Record<TrackerSourceId, string>> = {
  v2track: 'blue tones, one per aircraft',
  spidertracks: 'amber tones, one per aircraft',
  websentinel: 'purple',
  tracplus: 'light blue',
  opensky: 'orange',
  adsb: 'red',
  ozrunways: 'green',
  avplan: 'green',
  bushfireio: 'pink',
};

const SOURCE_BY_NAME = new Map(
  (Object.entries(TRACKER_SOURCE_NAME) as [TrackerSourceId, string][]).map(([id, name]) => [name, id]),
);

/** The tracker source of a map position (its `source` is the source's name, e.g. "V2Track"); null when unknown. */
export const trackerSourceOf = (name: string): TrackerSourceId | null => SOURCE_BY_NAME.get(name) ?? null;

/** The colours of one aircraft on the map: its icon and its trail. */
export interface AircraftColours {
  readonly icon: AircraftColour | 'fail' | 'accent';
  readonly trail: AircraftColour | 'accent';
}

type Coloured = Pick<AircraftPosition, 'id' | 'label' | 'source' | 'state' | 'rfds'>;

const byLabel = (x: Coloured, y: Coloured): number =>
  x.label < y.label ? -1 : x.label > y.label ? 1 : x.id < y.id ? -1 : x.id > y.id ? 1 : 0;

/**
 * Each aircraft's colours (decision 280): its source's colour; for V2Track and Spider Tracks a tone each, given in
 * registration order (then id), so every map window and laptop gives an aircraft the same tone whatever order the
 * feed lists it in (the Diurnal tool gave tones in the order aircraft first appeared, so two windows could differ);
 * the 17th aircraft of a source takes the first tone again, as the tool. The trail is the source colour. The icon is
 * the source colour too, except `fail` for SOS and lost tracking (with the words, brand book) and the aeromedical
 * body for an RFDS aircraft (decision 284). An aircraft on the ground whose tracker has not reported for the
 * lost-tracking minutes (state `stale`, "No recent position") is drawn grey, aircraft and trail, as the Diurnal tool's
 * #888888 (decision 326; `aircraft-stale`); an RFDS one keeps its shape in grey. A source Teslyn does not know
 * draws in `accent`.
 */
export function aircraftColours(aircraft: readonly Coloured[]): Map<string, AircraftColours> {
  const bySource = new Map<TrackerSourceId, Map<string, Coloured>>();
  for (const a of aircraft) {
    const s = trackerSourceOf(a.source);
    if (!s) continue;
    const list = bySource.get(s) ?? new Map<string, Coloured>();
    list.set(a.id, a);
    bySource.set(s, list);
  }
  const trail = new Map<string, AircraftColour>();
  for (const [source, list] of bySource) {
    const palette = AIRCRAFT_SOURCE_PALETTES[source];
    [...list.values()].sort(byLabel).forEach((a, i) => trail.set(a.id, palette[i % palette.length]!));
  }
  const out = new Map<string, AircraftColours>();
  for (const a of aircraft) {
    const t = trail.get(a.id) ?? 'accent';
    const alarm = a.state === 'sos' || a.state === 'lost';
    if (a.state === 'stale') out.set(a.id, { trail: 'aircraft-stale', icon: 'aircraft-stale' });
    else out.set(a.id, { trail: t, icon: alarm ? 'fail' : a.rfds ? 'aircraft-aeromedical' : t });
  }
  return out;
}

/** One row of the aircraft legend: a source on the map, its first colour and the words. */
export interface AircraftSourceLegendRow {
  readonly source: string;
  readonly colour: AircraftColour;
  readonly word: string;
}

/**
 * The sources of the aircraft on the map, in the default source order, each with its colour in words; then the grey
 * of "No recent position" when an aircraft on the map is stale (decision 326).
 */
export function aircraftSourceLegend(
  aircraft: readonly Pick<AircraftPosition, 'source' | 'state'>[],
): AircraftSourceLegendRow[] {
  const present = new Set(aircraft.map((a) => trackerSourceOf(a.source)));
  const rows: AircraftSourceLegendRow[] = LEGEND_ORDER.filter((s) => present.has(s)).map((s) => ({
    source: TRACKER_SOURCE_NAME[s],
    colour: AIRCRAFT_SOURCE_PALETTES[s][0]!,
    word: `${TRACKER_SOURCE_NAME[s]}: ${AIRCRAFT_SOURCE_COLOUR_WORD[s]}`,
  }));
  if (aircraft.some((a) => a.state === 'stale'))
    rows.push({
      source: TRACKER_STATE_WORD.stale,
      colour: 'aircraft-stale',
      word: `${TRACKER_STATE_WORD.stale}: grey`,
    });
  return rows;
}

/**
 * Every source's colour row and the grey of "No recent position", whatever is on the map: the map legend's "Show all"
 * (docs/specs/map.md §35.8).
 */
export function aircraftSourceLegendAll(): AircraftSourceLegendRow[] {
  return aircraftSourceLegend([
    ...LEGEND_ORDER.map((s) => ({ source: TRACKER_SOURCE_NAME[s] })),
    { source: '', state: 'stale' as const },
  ]);
}

// ---------------------------------------------------------------------------------------------- clients (Q36, Q37, Q47)

/** Whether an assignment covers a time. */
export function assignmentCovers(a: TrackerAssignment, timeMs: number): boolean {
  if (a.deleted_at) return false;
  const from = a.starts_at ? Date.parse(a.starts_at) : -Infinity;
  const to = a.ends_at ? Date.parse(a.ends_at) : Infinity;
  return timeMs >= from && timeMs <= to;
}

/**
 * What a client of `projectId` sees: the tenant's aircraft and vehicles assigned to the project at the time of each
 * fix (Q47), never public air traffic (decision 765, replacing decision 63: public traffic is for staff only on every
 * map; `clients_see_public_traffic` is no longer read). Never SOS or lost tracking (Q37): an SOS or lost aircraft
 * shows as its plain position, and no state word is passed on.
 */
export function clientView(
  aircraft: readonly {
    readonly position: TrackedPosition;
    readonly asset: TrackerAsset | null;
    readonly map: AircraftPosition;
  }[],
  assignments: readonly TrackerAssignment[],
  projectId: string,
  _settings?: TrackingSettings,
): AircraftPosition[] {
  void _settings; // kept for callers; the old client setting is no longer read (decision 765)
  const out: AircraftPosition[] = [];
  for (const a of aircraft) {
    // decision 765: public air traffic is never on a client's map
    if (!(FLEET_PROVIDERS as readonly string[]).includes(a.position.source)) continue;
    const ok =
      a.asset !== null &&
      assignments.some(
        (s) =>
          s.project_id === projectId && s.asset_id === a.asset!.id && assignmentCovers(s, a.position.timeMs),
      );
    if (!ok) continue;
    const { state: _s, ...plain } = a.map;
    void _s;
    out.push({ ...plain, airborne: isAirborne(a.position, a.map.kind) });
  }
  return out;
}
